// Import posthog - it will be initialized by the app before use
import posthog from 'posthog-js';

/**
 * Cached PostHog client (checked at module initialization)
 */
let cachedPostHogClient = null;

/**
 * Initialize PostHog client check (called at module load)
 */
function initializePostHogClient() {
    if (typeof window === 'undefined') {
        return;
    }

    // Check if PostHog is initialized (has config with api_key)
    if (posthog && posthog.config && posthog.config.api_key) {
        cachedPostHogClient = posthog;
    }
}

/**
 * Get PostHog client (synchronous, returns cached value)
 * @returns {object|null} - PostHog client if available and initialized, null otherwise
 */
function getPostHogClient() {
    return cachedPostHogClient;
}

// Check PostHog initialization when module loads
initializePostHogClient();

// Also check periodically in case PostHog initializes later
if (typeof window !== 'undefined') {
    // Check again after a short delay in case PostHog initializes asynchronously
    setTimeout(() => {
        initializePostHogClient();
    }, 100);
}

const nfExtensions = {
    "1.11.0": {
        "collection-search": true,
        "paapi": true,
        "amazon-scrape": false,
        "product-boxes": true
    },
    "1.10.0": {
        "collection-search": true,
        "amazon-scrape": false,
        "product-boxes": true
    },
    "1.9.0": {
        "collection-search": true,
        "amazon-scrape": false,
        "product-boxes": true
    },
    "1.8.0": {
        "collection-search": true,
        "amazon-scrape": false,
        "product-boxes": true
    },
    "1.7.0": {
        "collection-search": true,
        "amazon-scrape": false,
        "product-boxes": true
    },
    "1.6.0": {
        "collection-search": true,
        "amazon-scrape": false,
        "product-boxes": true
    },
    "1.5.0": {
        "collection-search": true,
        "amazon-scrape": false,
        "product-boxes": true
    },
    "1.4.0": {
        "collection-search": true,
        "amazon-scrape": false,
        "product-boxes": true
    },
    "1.3.0": {
        "collection-search": true,
        "amazon-scrape": false,
        "product-boxes": true
    },
    "1.2.0": {
        "collection-search": true,
        "amazon-scrape": false,
        "product-boxes": true
    },
    "1.1.0": {
        "collection-search": true,
        "amazon-scrape": false,
        "product-boxes": true
    },
    "1.0.10": {
        "collection-search": true,
        "amazon-scrape": false,
        "product-boxes": true
    },
    "1.0.9": {
        "collection-search": true,
        "amazon-scrape": false,
        "product-boxes": true
    },
    "1.0.8": {
        "collection-search": true,
        "amazon-scrape": false,
        "product-boxes": true
    },
    "1.0.7": {
        "collection-search": true,
        "amazon-scrape": false,
        "product-boxes": true
    },
    "1.0.6": {
        "collection-search": true,
        "amazon-scrape": false,
        "product-boxes": true
    },
    "1.0.4": {
        "collection-search": true,
        "amazon-scrape": false,
        "product-boxes": true
    },
    "1.0.3": {
        "collection-search": true,
        "amazon-scrape": false,
        "product-boxes": true
    },
    "1.0.2": {
        "amazon-scrape": false,
        "product-boxes": true
    },
    "1.0.1": {
        "amazon-scrape": true
    }
};

const NF_EXT_MANIFEST_URLS = {
    local: "http://localhost:4500/dist/nf-ext-version.json",
    qa: "https://s3.us-east-1.amazonaws.com/cdn.getpoln.com/creatorboost/widget/qa/nf-ext-version.json",
    prod: "https://s3.us-east-1.amazonaws.com/cdn.getpoln.com/creatorboost/widget/nf-ext-version.json"
};

/**
 * Optional hints from configure() used to resolve manifest URL.
 */
let siteApiBaseUrl = null;
let configuredVersionManifestUrl = null;
let configuredProfile = null;

/**
 * Cached current extension version (loaded at module initialization)
 */
let cachedExtensionVersion = null;

/**
 * Promise that resolves when the extension version is loaded
 */
let versionLoadPromise = null;

/** Max time to wait for nf-ext-version.json before giving up. */
const MANIFEST_TIMEOUT_MS = 3000;

/**
 * Get the version load promise (for testing purposes)
 * @returns {Promise<string|null>|null} - The version load promise or null
 */
export function getVersionLoadPromise() {
    return versionLoadPromise;
}

/**
 * Compare two semantic versions
 * @param {string} version1 - First version to compare
 * @param {string} version2 - Second version to compare
 * @returns {number} - Returns 1 if version1 > version2, -1 if version1 < version2, 0 if equal
 */
function compareVersions(version1, version2) {
    const v1Parts = version1.split('.').map(Number);
    const v2Parts = version2.split('.').map(Number);

    // Pad shorter version with zeros
    const maxLength = Math.max(v1Parts.length, v2Parts.length);
    while (v1Parts.length < maxLength) v1Parts.push(0);
    while (v2Parts.length < maxLength) v2Parts.push(0);

    for (let i = 0; i < maxLength; i++) {
        if (v1Parts[i] > v2Parts[i]) return 1;
        if (v1Parts[i] < v2Parts[i]) return -1;
    }

    return 0;
}

function normalizeProfile(profile) {
    if (!profile || typeof profile !== 'string') {
        return null;
    }
    const normalized = profile.trim().toLowerCase();
    if (normalized === 'prod' || normalized === 'production') {
        return 'prod';
    }
    if (normalized === 'qa') {
        return 'qa';
    }
    if (normalized === 'local' || normalized === 'dev' || normalized === 'development') {
        return 'local';
    }
    return null;
}

function inferProfileFromContext() {
    const fromConfiguredProfile = normalizeProfile(configuredProfile);
    if (fromConfiguredProfile) {
        return fromConfiguredProfile;
    }

    if (typeof window !== 'undefined') {
        const hostname = (window.location && window.location.hostname) || '';
        if (
            hostname === 'localhost' ||
            hostname === '127.0.0.1' ||
            hostname.endsWith('.localhost')
        ) {
            return 'local';
        }
        if (hostname.includes('qa')) {
            return 'qa';
        }
    }

    if (siteApiBaseUrl && typeof siteApiBaseUrl === 'string') {
        if (siteApiBaseUrl.includes('localhost') || siteApiBaseUrl.includes('127.0.0.1')) {
            return 'local';
        }
        if (siteApiBaseUrl.includes('qa')) {
            return 'qa';
        }
    }

    return 'prod';
}

function resolveVersionManifestUrl() {
    if (configuredVersionManifestUrl && typeof configuredVersionManifestUrl === 'string') {
        return configuredVersionManifestUrl;
    }
    const profile = inferProfileFromContext();
    if (profile === 'qa') {
        return NF_EXT_MANIFEST_URLS.qa;
    }
    if (profile === 'local') {
        return NF_EXT_MANIFEST_URLS.local;
    }
    return NF_EXT_MANIFEST_URLS.prod;
}

/**
 * Load the current extension version from a static manifest (async).
 * @returns {Promise<string|null>} - The version string or null if not found
 */
async function loadExtensionVersion() {
    try {
        const manifestUrl = resolveVersionManifestUrl();
        if (!manifestUrl) {
            return null;
        }

        // configure() is awaited before the app renders, so never let a slow manifest
        // request hold the page blank; on timeout nf-ext flags fall back to disabled.
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timer = controller ? setTimeout(() => controller.abort(), MANIFEST_TIMEOUT_MS) : null;
        let response;
        try {
            response = await fetch(manifestUrl, controller ? { signal: controller.signal } : undefined);
        } finally {
            if (timer) clearTimeout(timer);
        }
        if (!response.ok) {
            return null;
        }

        const data = await response.json();
        return data.version || null;
    } catch (error) {
        return null;
    }
}

/**
 * Get the current extension version (synchronous, returns cached value)
 * @returns {string|null} - The version string or null if not loaded yet
 */
export function getCurrentExtensionVersion() {
    return cachedExtensionVersion;
}

/**
 * Initialize extension version loading (called after configure()).
 * Fetches the extension version from the static manifest.
 */
function initializeExtensionVersion() {
    if (typeof window === 'undefined') {
        // Server-side, skip loading
        return;
    }

    if (versionLoadPromise) {
        // Already loading or loaded
        return;
    }

    versionLoadPromise = loadExtensionVersion().then(version => {
        cachedExtensionVersion = version;
        return version;
    }).catch(() => {
        cachedExtensionVersion = null;
        return null;
    });
}

/**
 * Get the list of enabled nf-ext feature names for the current extension version.
 * Used by help search to filter content by feature (e.g. product-boxes, amazon-scrape).
 * @returns {string[]} - Array of enabled feature names, or [] if version not loaded
 */
export function getEnabledNfExtFeatures() {
    const version = cachedExtensionVersion;
    if (!version) {
        return [];
    }
    const versionFeatures = nfExtensions[version];
    if (!versionFeatures || typeof versionFeatures !== 'object') {
        return [];
    }
    return Object.keys(versionFeatures).filter(name => versionFeatures[name] === true);
}

/**
 * Configure the flags package with required settings.
 * Must be called (and awaited) before nf-ext flags will work.
 *
 * @example
 * ```js
 * import { configure } from 'pp-flags';
 * await configure({
 *   profile: 'qa',
 *   versionManifestUrl: 'https://s3.us-east-1.amazonaws.com/cdn.getpoln.com/creatorboost/widget/qa/nf-ext-version.json'
 * });
 * // Now isFeatureEnabled('nf-ext:...') will have the version loaded
 * ```
 *
 * @param {Object} options - Configuration options
 * @param {string} [options.siteApiBaseUrl] - Optional site API URL (used as fallback hint for profile inference)
 * @param {string} [options.versionManifestUrl] - Optional explicit URL for nf-ext-version.json
 * @param {string} [options.profile] - Optional profile hint: local|qa|prod
 * @returns {Promise<string|null>} - Resolves with the loaded version, or null if loading failed
 */
export async function configure({ siteApiBaseUrl: url, versionManifestUrl, profile } = {}) {
    if (url) {
        siteApiBaseUrl = url;
    }
    if (versionManifestUrl) {
        configuredVersionManifestUrl = versionManifestUrl;
    }
    if (profile) {
        configuredProfile = profile;
    }

    // Trigger version loading now that configuration hints are available
    initializeExtensionVersion();

    // Wait for version to finish loading before returning
    if (versionLoadPromise) {
        return versionLoadPromise;
    }

    return null;
}


/**
 * Check if a featured version is enabled by comparing with XML version or PostHog feature flag
 *
 * This function uses the global PostHog instance (initialized via posthog.init() in your app).
 * The XML version and PostHog client are pre-loaded when the module is imported.
 *
 * @example Using in React components:
 * ```jsx
 * import { isFeatureEnabled } from 'pp-flags';
 *
 * function MyComponent() {
 *   // Synchronous check - no async/await needed
 *   const isEnabled = isFeatureEnabled('ph:my-feature');
 *   const isProductBoxesEnabled = isFeatureEnabled('nf-ext:product-boxes');
 *   const isVersionOk = isFeatureEnabled('nf-ext:version:1.0.3');
 *   // ...
 * }
 * ```
 *
 * @param {string} flagString - Flag string in format "nf-ext:feature-name" (e.g., "nf-ext:product-boxes"), "nf-ext:version:1.0.3", or "ph:feature-flag-name"
 * @returns {boolean} - True if enabled, false otherwise. Returns false if version/PostHog not loaded yet.
 */
export function isFeatureEnabled(flagString) {
    try {
        // Extract version from parameter string
        if (!flagString || typeof flagString !== 'string') {
            return false;
        }

        // Check if this is a PostHog feature flag (starts with "ph:")
        if (flagString.startsWith('ph:')) {
            const featureFlagName = flagString.substring(3); // Get everything after "ph:"
            if (!featureFlagName) {
                return false;
            }

            // Get PostHog client (cached at module load)
            const posthogClient = getPostHogClient();

            // Check if PostHog is available and has the method
            if (posthogClient && typeof posthogClient.isFeatureEnabled === 'function') {
                try {
                    return posthogClient.isFeatureEnabled(featureFlagName) || false;
                } catch (error) {
                    // Return false if PostHog check fails
                    return false;
                }
            }
            // Return false if PostHog is not available or not initialized
            return false;
        }

        // Handle nf-ext feature check (e.g., "nf-ext:product-boxes" or "nf-ext:version:1.0.3")
        if (flagString.startsWith('nf-ext:')) {
            const afterPrefix = flagString.substring(7); // Get everything after "nf-ext:"
            if (!afterPrefix) {
                return false;
            }

            // Get current version from cache (loaded at module initialization)
            const currentVersion = getCurrentExtensionVersion();
            if (!currentVersion) {
                // Version not loaded yet, return false
                return false;
            }

            // Check if this is a version comparison (nf-ext:version:XXX)
            if (afterPrefix.startsWith('version:')) {
                const specifiedVersion = afterPrefix.substring(8); // Get everything after "version:"
                if (!specifiedVersion) {
                    return false;
                }

                // Compare versions: return true if currentVersion >= specifiedVersion
                // This means: show content when the user's extension has at least the specified version
                const comparison = compareVersions(currentVersion, specifiedVersion);
                return comparison >= 0;
            }

            // Otherwise, treat it as a feature name (e.g., "nf-ext:product-boxes")
            const featureName = afterPrefix;

            // Look up features for this version in nfExtensions
            const versionFeatures = nfExtensions[currentVersion];
            if (!versionFeatures) {
                return false;
            }

            // Check if the feature exists and is enabled
            return !!versionFeatures[featureName];
        }

        // Handle other prefixes (legacy support or future extensions)
        const parts = flagString.split(':');
        if (parts.length < 2) {
            return false;
        }

        // For other formats, return false (or implement custom logic)
        return false;
    } catch (error) {
        // Return false on any error
        return false;
    }
}
